// @ts-strict-ignore
import React from 'react';
import {
  View,
  TouchableOpacity,
  Pressable,
} from 'react-native';
import classnames from 'classnames';

import {
  Box,
  Inline,
  IBoxProps,
  BoxSizeOptions,
} from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  BodyText,
  Tag,
  UnderlineLink,
  PortalOverlay,
  PortalOverlayModal,
  Stack,
} from '../../../../ui';

import RatingStars from '../../../../RatingStars';
import Image from '../../../Image';

import { addImageSourceURLs, IReview } from '../../../../../modules/provider/reviews';

import { ProfileImageSwiper } from '../../ProfileImageSwiper';
import { ClientProviderProfile } from '../../../../../api/ProfileClientView';
import './Review.module.scss';
import { IBasicImage } from '../../../../../api/Providers/GalleryImages';

interface IReviewProps {
  image?: any;
  isProvider: boolean;
  provider: ClientProviderProfile;
  reviewImages?: Array<IBasicImage>;
  reviewText?: string;
  noPadding?: boolean;
  ratingCount?: number;
  reviewCount?: number;
  actionText?: string;
  numStars?: number;
  creationTime?: string;
  hideRespond?: boolean;
  hideSeparator?: boolean;
  hideTags?: boolean;
  hideImages?: boolean;
  isNewClient?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  getFacebookReviewShareUrl?: (review: IReview) => string;
  highlighted?: boolean;
  onReviewImageSelected?: (args: { id; imageCount: number }) => void;
  onReviewImageShown?: (args: { id; imageCount: number; imageIndex: number }) => void;
  openResponseModal?: (review: IReview, edit) => void;
  trackShare?: (review: IReview) => void;
  onAction?: (review: IReview) => void;
  condenseStars?: boolean;
  hideClient?: boolean;
  canPinAndShare: boolean;
  shouldShowRespondButton: boolean;
  pinned: boolean;
  canShare: boolean;
  onPinIconPress: () => void;
  onFacebookShare: () => void;
  onActionPress: () => void;
  onImagePress: (imageIndex: number) => void;
  onImageShown: (index: number) => void;
  onEdit: () => void;
  openGalleryImageAtIndex: number | null;
  resetOpenGalleryImageAtIndex: () => void;
  aboutHairType: string[];
  aboutProvider: string[];
  aboutLocation: string[];
  responseText?: string;
  serviceName: string;
  firstName: string;
  lastName: string;
  reviewDate: string;
  isInReviewTagFiltersTest?: boolean;
  boxProps: Omit<IBoxProps, 'style'>;
}

const PinkTagDisplay = ({ title, otherInfo }) => (
  <Inline
    justifyContent="flex-start"
    flexWrap="wrap"
    alignItems="center"
    space={4}
  >
    <BodyText>
      {title}
    </BodyText>
    {otherInfo.map((text: String, index: number) => (
      <Inline space={4} alignItems="center" key={`${text}`}>
        <BodyText weight="bold" color={BrandColor.FloraDark}>
          {text}
        </BodyText>
        {
          (index !== (otherInfo.length - 1)) && (
            <BodyText appearance="large" weight="bold" color={BrandColor.Silver}>•</BodyText>
          )
        }
      </Inline>
    ))}
  </Inline>
);

const PINNED_COLOR = BrandColor.GreenHighlight;

const Review = (props: IReviewProps) => {
  const {
    highlighted,
    isInReviewTagFiltersTest,
    noPadding,
    isFirst,
    isLast,
    numStars,
    ratingCount,
    reviewCount,
    condenseStars,
    serviceName,
    canPinAndShare,
    onPinIconPress,
    pinned,
    canShare,
    onFacebookShare,
    reviewText,
    hideTags,
    hideClient,
    aboutHairType,
    responseText,
    isProvider,
    firstName,
    lastName,
    reviewDate,
    isNewClient,
    actionText,
    onActionPress,
    hideSeparator,
    aboutProvider,
    provider,
    aboutLocation,
    shouldShowRespondButton,
    openGalleryImageAtIndex,
    resetOpenGalleryImageAtIndex,
    hideImages,
    reviewImages,
    onImagePress,
    onEdit,
    onImageShown,
    boxProps,
  } = props;

  const [shouldTruncateText, setShouldTruncateText] = React.useState<boolean>(true);

  const leftPadding: BoxSizeOptions = highlighted ? 8 : 12;
  const topPadding: BoxSizeOptions = highlighted ? 8 : 12;

  return (
    <Box paddingBottom={(isInReviewTagFiltersTest) ? 4 : 0}>
      <Inline
        testID="review__review-container"
        borderStyle="solid"
        borderColor={BrandColor.LightGrey}
        borderXWidth={noPadding ? undefined : 1}
        borderTopWidth={((noPadding || !isFirst) && !isInReviewTagFiltersTest) ? undefined : 1}
        borderBottomWidth={(noPadding || !isLast) ? undefined : 1}
        color={BrandColor.WhiteWash}
        maxWidth={noPadding ? '100%' : undefined}
        {...boxProps}
      >
        {highlighted && (
          <Box
            minHeight="100%"
            color={BrandColor.BoneLight}
            minWidth={4}
            maxWidth={4}
          />
        )}
        <Box
          paddingRight={noPadding ? 0 : 12}
          paddingLeft={noPadding ? 0 : leftPadding}
          paddingTop={noPadding ? 0 : topPadding}
          paddingBottom={noPadding ? 0 : 12}
          maxWidth="100%"
          minWidth="100%"
        >
          {highlighted && (
            <BodyText testID="review__highlighted-review-header" color={BrandColor.Ash} appearance="medium">
              Highlighted Review
            </BodyText>
          )}
          {!isInReviewTagFiltersTest ? (
            <React.Fragment>
              <Inline justifyContent="space-between">
                <Inline space={4} flexShrink={1}>
                  <RatingStars
                    ratingStars={numStars}
                    size={12}
                    ratingsCount={ratingCount}
                    reviewCount={reviewCount}
                    condensed={condenseStars}
                  />
                  <BodyText
                    testID="review__service-name"
                    weight="bold"
                    styleName="review-header"
                    whiteSpace="nowrap"
                    numberOfLines={1}
                  >
                    {serviceName}
                  </BodyText>
                </Inline>
                {canPinAndShare && (
                  <Inline space={8}>
                    <BodyText
                      onPress={onPinIconPress}
                      styleName={classnames('pinButton', { pinned })}
                      color={pinned ? PINNED_COLOR : undefined}
                      testID="review__pin"
                    />
                    {canShare && (
                      <BodyText
                        onPress={onFacebookShare}
                        styleName="shareFacebook"
                        testID="review__share-fb"
                      >
                        Share
                      </BodyText>
                    )}
                  </Inline>
                )}
              </Inline>
              {!!reviewText && (
                <Box testID="review__review-text" padding={4}>
                  <TouchableOpacity onPress={() => setShouldTruncateText(!shouldTruncateText)}>
                    <BodyText numberOfLines={shouldTruncateText ? 4 : undefined}>
                      &rdquo;{reviewText}&ldquo;
                    </BodyText>
                  </TouchableOpacity>
                </Box>
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <RatingStars
                ratingStars={numStars}
                size={12}
                ratingsCount={ratingCount}
                reviewCount={reviewCount}
                condensed={condenseStars}
              />
              <BodyText
                testID="review__service-name"
                weight="bold"
                styleName="review-header"
                whiteSpace="nowrap"
                numberOfLines={1}
              >
                {serviceName}
              </BodyText>
              {canPinAndShare && (
                <Inline space={8}>
                  <BodyText
                    onPress={onPinIconPress}
                    styleName={classnames('pinButton', { pinned })}
                    color={pinned ? PINNED_COLOR : undefined}
                    testID="review__pin"
                  />
                  {canShare && (
                    <BodyText
                      onPress={onFacebookShare}
                      styleName="shareFacebook"
                      testID="review__share-fb"
                    >
                      Share
                    </BodyText>
                  )}
                </Inline>
              )}
              {!!reviewText && (
                <Box testID="review__review-text" paddingY={4}>
                  <TouchableOpacity onPress={() => setShouldTruncateText(!shouldTruncateText)}>
                    <BodyText numberOfLines={shouldTruncateText ? 4 : undefined}>
                      {reviewText}
                    </BodyText>
                  </TouchableOpacity>
                </Box>
              )}
            </React.Fragment>
          )}

          {!hideTags && !isInReviewTagFiltersTest && (
            <Stack space={8} paddingY={8}>
              {/* About the pro tag list */}
              {aboutProvider.length > 0 && (
                <Stack space={8}>
                  <BodyText
                    testID="review__about-provider-header"
                    whiteSpace="nowrap"
                    weight="bold"
                  >
                    About {provider.name}:
                  </BodyText>
                  <Inline
                    testID="review__about-provider"
                    space={8}
                  >
                    {aboutProvider.map(tag => (
                      <Tag
                        key={tag}
                        color={BrandColor.BoneLight}
                        borderRadius={0}
                      >
                        {tag}
                      </Tag>
                    ))}
                  </Inline>
                </Stack>
              )}

              {/* About the location tag list */}
              {aboutLocation.length > 0 && (
                <Stack space={8}>
                  <BodyText
                    whiteSpace="nowrap"
                    testID="review__about-location-header"
                    weight="bold"
                  >
                    {provider.name}&apos;s Location:
                  </BodyText>
                  <Inline
                    testID="review__about-location"
                    space={8}
                  >
                    {aboutLocation.map(tag => (
                      <Tag
                        key={tag}
                        color={BrandColor.BoneLight}
                        borderRadius={0}
                      >
                        {tag}
                      </Tag>
                    ))}
                  </Inline>
                </Stack>
              )}
            </Stack>
          )}

          <View styleName="editActionsContainer">
            {!!shouldShowRespondButton && (
              <BodyText
                onPress={onEdit}
                styleName="respondButton"
                testID="review__respond-to-review-button"
              >
                Respond
              </BodyText>
            )}
          </View>

          {/* Client images */}
          {!hideImages && !!reviewImages.length && (
            <Box
              paddingTop={isInReviewTagFiltersTest ? 8 : 20}
              paddingBottom={isInReviewTagFiltersTest ? 8 : 20}
            >
              <Inline space={8}>
                {reviewImages.map((reviewImage, imageIndex) => {
                  const { id, source } = addImageSourceURLs(reviewImage);
                  return (
                    <Pressable
                      key={id}
                      onPress={() => onImagePress(imageIndex)}
                    >
                      <Image
                        testID={`review__image-${id}`}
                        source={source}
                        style={{
                          width: 100,
                          height: 100,
                        }}
                      />
                    </Pressable>
                  );
                })}
                {typeof openGalleryImageAtIndex === 'number' && (
                  <PortalOverlay
                    onBackdropClick={resetOpenGalleryImageAtIndex}
                    name="gallery-image-modal"
                  >
                    <PortalOverlayModal
                      position="fullScreen"
                      testID="review__profile-image-swiper-container"
                    >
                      <ProfileImageSwiper
                        provider={provider}
                        imageTotal={reviewImages.length}
                        images={reviewImages}
                        onCloseRequest={resetOpenGalleryImageAtIndex}
                        initialIndex={openGalleryImageAtIndex}
                        onIndexChanged={onImageShown}
                        formatImageUrl={img => `${img.url}_300x300.jpg`}
                        providerProfilePhoto={`${provider.profile_photo}_50x50.jpg`}
                      >
                        <Inline
                          space={12}
                          paddingY={4}
                          paddingLeft={16}
                          style={{ width: '100%', maxWidth: 540 }}
                        >
                          <RatingStars
                            ratingStars={numStars}
                          />
                          <BodyText
                            weight="bold"
                            numberOfLines={1}
                          >
                            {serviceName}
                          </BodyText>
                        </Inline>
                        {!!reviewText && (
                          <Box
                            paddingLeft={16}
                          >
                            <BodyText>
                              {`\u201D${reviewText}\u201C`}
                            </BodyText>
                          </Box>
                        )}
                      </ProfileImageSwiper>
                    </PortalOverlayModal>
                  </PortalOverlay>
                )}
              </Inline>
            </Box>
          )}
          {!(hideTags || hideClient) && aboutHairType.length > 0 && isInReviewTagFiltersTest && (
            <PinkTagDisplay title="Hair Type:" otherInfo={aboutHairType} />
          )}
          {!hideTags && aboutProvider.length > 0 && isInReviewTagFiltersTest && (
            <PinkTagDisplay title={`About ${provider.name}:`} otherInfo={aboutProvider} />
          )}
          {!hideTags && aboutLocation.length > 0 && isInReviewTagFiltersTest && (
            <PinkTagDisplay title="Location:" otherInfo={aboutLocation} />
          )}

          {/* Pro response text */}
          {!!responseText && (
            <View styleName="response" testID="review__response">
              <View styleName="colorBlock" />
              <View styleName="reponseContent">
                <View>
                  <BodyText styleName="responseText" testID="review__response-text">
                    <BodyText weight="bold">{provider.name}</BodyText>
                    {`: \u201D${responseText}\u201C`}
                  </BodyText>
                </View>
                {isProvider && (
                  <View>
                    <BodyText
                      onPress={onEdit}
                      styleName="editTag"
                      testID="review__response-edit"
                    >
                      Edit
                    </BodyText>
                  </View>
                )}
              </View>
            </View>
          )}
          <Inline justifyContent="space-between">
            <Inline
              space={4}
              paddingY={4}
              flexWrap="nowrap"
              testID="review__reviewer-name"
            >
              {!hideClient && (
                <BodyText
                  color={isInReviewTagFiltersTest ? BrandColor.Ash : BrandColor.Metal}
                  whiteSpace="nowrap"
                  appearance="small"
                  textTransform="uppercase"
                >
                  {firstName} {lastName ? `${lastName.substring(0, 1)}.` : ''}
                </BodyText>
              )}

              {!(hideTags || hideClient)
                && aboutHairType.length > 0 && !isInReviewTagFiltersTest && (
                  <React.Fragment>
                    <BodyText
                      color={BrandColor.Metal}
                      whiteSpace="nowrap"
                      appearance="small"
                      textTransform="none"
                    >
                      &nbsp;| Hair type:
                    </BodyText>
                    <BodyText
                      color={BrandColor.Metal}
                      whiteSpace="nowrap"
                      appearance="small"
                      textTransform="none"
                    >
                      {aboutHairType.join(' ')}
                    </BodyText>
                  </React.Fragment>
              )}
              <BodyText
                color={isInReviewTagFiltersTest ? BrandColor.Ash : BrandColor.Metal}
                whiteSpace="nowrap"
                appearance="small"
                testID="review__review-date"
              >
                {reviewDate}
              </BodyText>
              {!!isNewClient && !hideClient && (
                <BodyText
                  color={BrandColor.Metal}
                  whiteSpace="nowrap"
                  appearance="small"
                  textTransform="uppercase"
                >
                  1st Time Client
                </BodyText>
              )}
            </Inline>
            {!!actionText && (
              <UnderlineLink
                textAppearance="medium"
                textWeight="bold"
                onPress={onActionPress}
                testID="review__on-action-button"
              >
                {actionText}
              </UnderlineLink>
            )}
          </Inline>
        </Box>
      </Inline>

      {!(hideSeparator || isLast) && <View styleName="separator" />}
    </Box>
  );
};

export default Review;
